/* eslint-disable max-lines */
import { createAction, handleActions } from "redux-actions";
import config from "appConfig";
import {
  api,
  checkEmptyObject,
  formatDate,
  generateUUID,
  getMultiLevelField,
  isAllowedValue,
  notification,
  getAllMultiLevelField,
  localStorage
} from "../../services";
import { ControlType, AdsSortOption, PageRoute, AllTypeCategory, LocalStorageKey } from "../../models";
import { getSummaryConfig } from "../../dataConfig";
import { getImageLink, getImageUrl } from "../../services/asset";
import history from "../../history";
import ReactGA from "react-ga4";
import {
  rootReducerInterface,
  searchInterface,
  UserI
} from "../../typings/interfaces";
import {
  AdListI,
  BasicOptionI,
  ControlChildI,
  ControlConfigI,
  ControlFieldsI,
  ControlI,
  GetSaveSearchCategoryI,
  GetSaveSearchModuleI,
  QueryParamsI,
  SavedSearchDataI,
  SavedSearchI,
  SaveSearchI,
  SearchConfigCategoryI,
  SearchConfigI
} from "../../typings/routesTypes";
import { mapModulesAndCategories, mapModuleToEn } from "../LanguageMapper";

const SEARCH_CONTROLS_GET = "SEARCH_CONTROLS_GET";
const SEARCH_CONTROLS_RESET = "SEARCH_CONTROLS_RESET";
const SEARCH_CONTROLS_DELETE = "SEARCH_CONTROLS_DELETE";
const AD_SEARCH = "AD_SEARCH";
const AD_CLEAR_SEARCH = "AD_CLEAR_SEARCH";
const SEARCH_CONTROLS_SAVE = "SEARCH_CONTROLS_SAVE";
const SAVED_SEARCH_GET = "SAVED_SEARCH_GET";
const SAVED_SEARCH_DELETE = "SAVED_SEARCH_DELETE";
const SAVED_SEARCH_UPDATE = "SAVED_SEARCH_UPDATE";
const SAVED_SEARCH_LOADING_START = "SAVED_SEARCH_LOADING_START";
const CRITERIA_MODULES_UPDATE = "CRITERIA_MODULES_UPDATE";
const IS_FOCUS = "IS_FOCUS";
const SEARCH_LOADING = "SEARCH_LOADING";

// Track pending deletes to prevent fast-clicking
const pendingSavedSearchDeletes: string[] = [];

// Track if saved searches are currently being fetched
let isLoadingSavedSearches = false;

// Action for tracking loading state
export const startLoadingSavedSearches = createAction(SAVED_SEARCH_LOADING_START);

// eslint-disable-next-line complexity,max-lines-per-function
const mapFieldsToConfig = (
  controlFields: ControlFieldsI,
  control: ControlI,
  controlConfig: ControlConfigI
) => {
  const result = controlFields;

  switch (control.type) {
    case ControlType.inputLocation: {
      const setLevelFields = (child: ControlChildI, clonedControl?: any) => {
        if (child.child) {
          setLevelFields(child.child, clonedControl);
        }

        if (clonedControl) {
          // eslint-disable-next-line
          child.field = child.excludedField;

          result[child.excludedField] = {
            ...clonedControl
          };
        } else {
          result[child.field] = {
            ...controlConfig,
            id: control.id
          };
        }
      };

      setLevelFields(control.child);

      if (control.excluded) {
        let clonedControl = JSON.parse(JSON.stringify(control));
        clonedControl = {
          ...clonedControl,
          tagFormat: clonedControl.excluded.tagFormat,
          id: generateUUID()
        };

        setLevelFields(clonedControl.child, clonedControl);
      }
      break;
    }

    case ControlType.multiLevel: {
      const setLevelFields = (child: ControlChildI, clonedControl?: any) => {
        if (child.child) {
          setLevelFields(child.child, clonedControl);
        }

        if (clonedControl) {
          // eslint-disable-next-line
          child.field = child.excludedField;

          result[child.excludedField] = {
            ...clonedControl
          };
        } else {
          result[child.field] = {
            ...controlConfig,
            id: control.id
          };
        }
      };

      setLevelFields(control.child);

      if (control.excluded) {
        let clonedControl = JSON.parse(JSON.stringify(control));
        clonedControl = {
          ...clonedControl,
          tagFormat: clonedControl.excluded.tagFormat,
          id: generateUUID()
        };

        setLevelFields(clonedControl.child, clonedControl);
      }
      break;
    }

    case ControlType.singleSelector:
    case ControlType.multiSelector:
    case ControlType.multiColorSelector:
      // eslint-disable-next-line
      controlFields[control.field] = {
        ...controlConfig,
        id: control.id
      };
      break;

    case ControlType.range:
      // eslint-disable-next-line
      controlFields[control.fromField] = {
        type: control.type,
        numberFormatted: control.numberFormatted,
        tagFormat: control.fromTagFormat,
        isDateValue: control.isDateValue,
        id: control.id
      };
      // eslint-disable-next-line
      controlFields[control.toField] = {
        type: control.type,
        numberFormatted: control.numberFormatted,
        tagFormat: control.toTagFormat,
        isDateValue: control.isDateValue,
        id: control.id
      };
      break;

    case ControlType.multiFieldSelector:
      control.options.forEach(option => {
        // eslint-disable-next-line
        controlFields[option.field] = {
          ...controlConfig,
          id: control.id
        };
      });
      break;

    case ControlType.checkbox:
      control.options = [{ title: control.title, field: control.field }]; // eslint-disable-line
      // eslint-disable-next-line
      controlFields[control.field] = {
        ...controlConfig,
        id: control.id
      };
      break;

    default:
      break;
  }

  return result;
};

// eslint-disable-next-line complexity,max-lines-per-function
const parseControlValues = (control: ControlI, queryParams: QueryParamsI) => {
  if (!queryParams) {
    return undefined;
  }

  const data = {};
  switch (control.type) {
    case ControlType.singleSelector:
    case ControlType.multiSelector:
    case ControlType.multiColorSelector:
    case ControlType.checkbox:
      if (isAllowedValue(queryParams[control.field])) {
        data[control.field] = queryParams[control.field];
      }
      break;

    case ControlType.inputLocation: {
      const fields = [];
      getAllMultiLevelField(control.child, fields);
      fields.forEach(field => {
        if (isAllowedValue(queryParams[field])) {
          data[field] = queryParams[field];
        }
      });
      break;
    }

    case ControlType.multiLevel: {
      const fields = [];
      if (!control.excluded) {
        getMultiLevelField(control.child, fields);
      } else {
        // for group multilevel control
        getAllMultiLevelField(control.child, fields);
      }

      fields.forEach(field => {
        if (isAllowedValue(queryParams[field])) {
          data[field] = queryParams[field];
        }
      });

      break;
    }

    case ControlType.range: {
      if (isAllowedValue(queryParams[control.fromField])) {
        data[control.fromField] = queryParams[control.fromField];
      }

      if (isAllowedValue(queryParams[control.toField])) {
        data[control.toField] = queryParams[control.toField];
      }
      break;
    }

    case ControlType.multiFieldSelector: {
      const { options } = control;

      options.forEach((option: BasicOptionI) => {
        if (option.field && isAllowedValue(queryParams[option.field])) {
          data[option.field] = queryParams[option.field];
        }
      });
      break;
    }

    default:
      break;
  }

  return checkEmptyObject(data);
};

const showSavedSearchNotification = () => {
  const actionButton = {
    text: "PREGLEDAJ",
    onClick: () => {
      ReactGA.event({
        category: "Spremanje pretrage",
        action: "Click",
        label: "Pregled spremljene pretrage"
      });
      history.push(PageRoute.savedSearch);
    }
  };

  notification.showHiddenAction(
    "Pretraga je spremljena.",
    actionButton
  );
};

// Helper function to check if an item is a control (has "type" property) or a nested group (has "controls" array)
var isNestedGroup = function(item: any): boolean {
  return item && !item.type && item.controls && Array.isArray(item.controls);
};

// Helper function to flatten nested groups structure
// Some groups have nested "groups" with sub-groups (e.g., "Oprema vozila" with "Sigurnost", "Udobnost", etc.)
// This function flattens them so each sub-group becomes its own top-level group
const flattenNestedGroups = (groups: any[]): any[] => {
  if (!groups) return [];

  const result: any[] = [];

  for (var i = 0; i < groups.length; i++) {
    var group = groups[i];

    // If group has controls directly
    if (group.controls && Array.isArray(group.controls)) {
      // Check if controls are actual controls or nested groups
      // Nested groups have "controls" array but no "type" property
      var hasNestedGroups = group.controls.length > 0 && group.controls.some(isNestedGroup);

      if (hasNestedGroups) {
        // Flatten nested groups - each sub-group becomes its own top-level group
        for (var j = 0; j < group.controls.length; j++) {
          var subGroup = group.controls[j];
          if (isNestedGroup(subGroup)) {
            result.push({
              name: subGroup.name || group.name,
              controls: subGroup.controls
            });
          }
        }
      } else {
        // Regular group with actual controls
        result.push(group);
      }
    }
    // If group has nested groups (sub-groups), flatten them
    else if (group.groups && Array.isArray(group.groups)) {
      for (var k = 0; k < group.groups.length; k++) {
        var nestedGroup = group.groups[k];
        // Only add sub-groups that have controls
        if (nestedGroup.controls && Array.isArray(nestedGroup.controls)) {
          result.push({
            name: nestedGroup.name || group.name,
            controls: nestedGroup.controls
          });
        }
      }
    }
  }

  return result;
};

// module / category (API names) the controls of a search config belong to
const controlScope = (
  searchConfig: SearchConfigI,
  categoryConfig: SearchConfigCategoryI | null,
  module: string
) => ({
  module: (searchConfig && searchConfig.name) || mapModuleToEn(module),
  category: categoryConfig ? categoryConfig.name : undefined
});

// Actions
export const getControls = createAction(
  SEARCH_CONTROLS_GET,
  // eslint-disable-next-line complexity
  async (
    module: string,
    category: string,
    queryParams: QueryParamsI,
    getConfigData: Function
  ) => {
    const searchConfig: SearchConfigI = await getConfigData(
      module || queryParams.module
    );

    if (!searchConfig) {
      return null;
    }

    const categorySearchConfig = searchConfig.categories.find(
      c =>
        c.nameHr === category ||
        c.name === category ||
        c.nameHr === queryParams.category ||
        c.name === queryParams.category
    );

    const categoryConfig =
      category && categorySearchConfig
        ? categorySearchConfig
        : searchConfig.categories.find(
          c =>
            c.nameHr === AllTypeCategory.name ||
            c.name === AllTypeCategory.name
        ) || null;

    const rawGroups = categoryConfig ? categoryConfig.groups : [];
    const groups = flattenNestedGroups(rawGroups);

    // Scope of the controls: the multi level controls (makes, subcategories)
    // fetch their options for the module / category they are configured in.
    const scope = controlScope(searchConfig, categoryConfig, module || queryParams.module);

    let controlFields: any = {};
    const mappedControls = groups.map(function(group) {
      var controls = group.controls || [];
      return {
        name: group.name,
        controls: controls.map(function(rawControl: any) {
          var c = { ...rawControl, ...scope };
          var control = {
            ...c,
            id: generateUUID(),
            data: parseControlValues(c, queryParams)
          };
          controlFields = mapFieldsToConfig(controlFields, control, c);
          return control;
        })
      };
    });

    return { controls: mappedControls, controlFields };
  }
);

export const deleteControls = createAction(SEARCH_CONTROLS_DELETE, async () => {
  return { controls: [] };
});

export const resetControls = createAction(
  SEARCH_CONTROLS_RESET,
  // eslint-disable-next-line complexity
  async (
    module: string,
    category: string,
    queryParams: QueryParamsI,
    getConfigData: Function
  ) => {
    const searchConfig: SearchConfigI = await getConfigData(
      module || queryParams.module
    );

    if (!searchConfig) {
      return null;
    }

    const categorySearchConfig = searchConfig.categories.find(
      c => c.name === category || c.name === queryParams.category
    );

    const categoryConfig =
      category && categorySearchConfig
        ? categorySearchConfig
        : searchConfig.categories[0] || null;

    const rawGroups = categoryConfig ? categoryConfig.groups : [];
    const groups = flattenNestedGroups(rawGroups);

    const scope = controlScope(searchConfig, categoryConfig, module || queryParams.module);

    let controlFields: any = {};
    const mappedControls = groups.map(function(group) {
      var controls = group.controls || [];
      return {
        name: group.name,
        controls: controls.map(function(rawControl: any) {
          var c = { ...rawControl, ...scope };
          var control = {
            ...c,
            id: generateUUID(),
            data: undefined
          };
          controlFields = mapFieldsToConfig(controlFields, control, c);
          return control;
        })
      };
    });

    return { controls: mappedControls, controlFields };
  }
);

export const searchAds = createAction(
  AD_SEARCH,
  async (
    {
      module,
      queryParams,
      favoriteIds = [],
      itemPerPage = config.maxAdPerPage
    },
    callback: Function
  ) => {
    const searchParams = queryParams.text
      ? {
          ...queryParams,
          module,
          sortOption: queryParams.sortOption || AdsSortOption.DateDesc,
          itemPerPage,
          text: queryParams.text.toLowerCase()
        }
      : {
          ...queryParams,
          module,
          sortOption: queryParams.sortOption || AdsSortOption.DateDesc,
          itemPerPage,
          text: undefined
        };

    try {
      const adList: AdListI = await api.Ads.list(searchParams);

      const data = await Promise.all(
        adList.data.map(async ad => {
          const summaryConfig = await getSummaryConfig(ad.module);

          return api.parseSummaryData(summaryConfig, ad, favoriteIds);
        })
      );

      callback && callback(true);

      return {
        page: adList.currentPage,
        count: adList.count,
        data
      };
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);

export const clearAdsSearchResults = createAction(AD_CLEAR_SEARCH, () => {
  return null;
});

export const saveSearch = createAction(
  SEARCH_CONTROLS_SAVE,
  async (data: SaveSearchI, callback: Function) => {
    try {
      let filteredData = {
        ...mapModulesAndCategories(data),
        criteria: mapModulesAndCategories(data.criteria)
      };

      await api.SavedSearch.create(filteredData);
      showSavedSearchNotification();

      callback && callback(true);
      return null;
    } catch (error) {
      callback && callback(false);
      return null;
    }
  }
);

export const syncSavedSearch = createAction(
  SEARCH_CONTROLS_SAVE, 
  async (user: UserI, callback: Function) => {
    const savedSearch = localStorage.getItem(LocalStorageKey.savedSearch);
    
    if(savedSearch && checkEmptyObject(user)) {
      try {
        let filteredData = {
          ...mapModulesAndCategories(savedSearch),
          criteria: mapModulesAndCategories(savedSearch.criteria)
        };
  
        await api.SavedSearch.create(filteredData);
        localStorage.removeItem(LocalStorageKey.savedSearch);
        showSavedSearchNotification();
        callback && callback(true);
        return null;
      } catch (error) {
        localStorage.removeItem(LocalStorageKey.savedSearch);
        callback && callback(false);
        return null;
      }
    }
  }
);

export const getSavedSearch = createAction(
  SAVED_SEARCH_GET,
  async (
    categories: GetSaveSearchCategoryI[],
    modules: GetSaveSearchModuleI[], // eslint-disable-line
    module: string,
    page: number
  ) => {
    try {
      // Set loading flag
      isLoadingSavedSearches = true;

      const queries = {
        page,
        module,
        itemPerPage: config.maxSavedSearchPerPage
      };
      const savedSearches: SavedSearchI = await api.SavedSearch.get(queries);
      const savedSearchList = savedSearches.data;

      savedSearchList.forEach(item => {
        const category = categories.find(
          c => c.moduleName === item.module && c.name === item.category
        );
        const module = modules.find(m => m.name === item.module);

        if (category) {
          item.imageId = getImageLink(category.iconId); // eslint-disable-line
        } else if(module) {
          item.imageId = getImageLink(module.iconId); // eslint-disable-line
        } else{
          item.imageId = getImageUrl(config.imagePlaceholder); // eslint-disable-line
        }

        // eslint-disable-next-line
        item.formattedCreatedTime = formatDate(
          item.createdTime,
          config.dateFormat
        );
        if (item.category) {
          item.criteria = { ...item.criteria, category: item.category }; // eslint-disable-line
        }
      });

      // Clear loading flag
      isLoadingSavedSearches = false;

      return {
        count: savedSearches.count,
        data: savedSearchList,
        page: savedSearches.currentPage,
        isLoading: false
      };
    } catch (error) {
      // Clear loading flag on error
      isLoadingSavedSearches = false;
      return { isLoading: false };
    }
  }
);

export const deleteSavedSearch = createAction(
  SAVED_SEARCH_DELETE,
  async (id: string, savedSearches: any, onComplete?: Function) => {
    // Don't allow delete while saved searches are being fetched
    if (isLoadingSavedSearches) {
      return null;
    }

    // If item is already being deleted, do nothing
    if (pendingSavedSearchDeletes.includes(id)) {
      return null;
    }

    // Add to pending array
    pendingSavedSearchDeletes.push(id);

    try {
      await api.SavedSearch.delete(id);
      notification.show(
        "Uspješno brisanje"
      );

      // Remove from pending array
      const index = pendingSavedSearchDeletes.indexOf(id);
      if (index > -1) {
        pendingSavedSearchDeletes.splice(index, 1);
      }

      // Call the callback if provided (for reloading data after deletion)
      if (onComplete) {
        onComplete();
      }

      // Optimistically update the state
      if (savedSearches && savedSearches.data) {
        return {
          count: savedSearches.count - 1,
          data: savedSearches.data.filter((item: any) => item.id !== id),
          page: savedSearches.page
        };
      }

      return {};
    } catch (error) {
      // Remove from pending on error
      const index = pendingSavedSearchDeletes.indexOf(id);
      if (index > -1) {
        pendingSavedSearchDeletes.splice(index, 1);
      }
      return null;
    }
  }
);

export const updateSavedSearch = createAction(
  SAVED_SEARCH_UPDATE,
  async (id: string, data: SavedSearchDataI, savedSearches: SavedSearchI) => {
    try {
      await api.SavedSearch.update(id, data);
      notification.show(
        "Uspješno ažuriranje"
      );

      savedSearches.data.forEach(savedSearch => {
        if (savedSearch.id === id) {
          savedSearch.notifyFrequency = data.notifyFrequency; // eslint-disable-line
        }
      });

      return savedSearches;
    } catch (error) {
      return null;
    }
  }
);

export const updateCriteriaModules = createAction(
  CRITERIA_MODULES_UPDATE,
  (module: string, url: string) => ({ module, url })
);

export const setIsFocus = createAction(IS_FOCUS, (state: boolean) => {
  return state;
});

export const setSearchLoading = createAction(SEARCH_LOADING, (state: boolean) => {
  return state;
});

// Selectors
export const selectControls = (state: rootReducerInterface) =>
  state.search.controls;

export const selectControlFields = (state: rootReducerInterface) =>
  state.search.controlFields;

export const selectResults = (state: rootReducerInterface) => state.search.ads;

export const selectSavedSearch = (state: rootReducerInterface) =>
  state.search.savedSearches;

export const selectIsLoadingSavedSearches = (state: rootReducerInterface) =>
  state.search.isLoadingSavedSearches;

export const selectCriteriaModules = (state: rootReducerInterface) =>
  state.search.criteriaModules;

export const selectIsFocus = (state: rootReducerInterface) =>
  state.search.isFocus;

export const selectSearchLoading = (state: rootReducerInterface) =>
  state.search.isSearchLoading;

// Reducer
const initialState: searchInterface = {
  controls: [],
  ads: null,
  savedSearches: null,
  controlFields: null,
  criteriaModules: {},
  isFocus: false,
  isLoadingSavedSearches: false,
  isSearchLoading: false
};

const setSavedSearchState = (state: searchInterface, { payload }) => ({
  ...state,
  savedSearches: payload || {}
});

export default handleActions(
  {
    [SEARCH_CONTROLS_GET]: (state: searchInterface, { payload }) => ({
      ...state,
      ...payload
    }),
    [SEARCH_CONTROLS_RESET]: (state: searchInterface, { payload }) => {
      return {
        ...state,
        controls: payload.controls
      };
    },
    [SEARCH_CONTROLS_DELETE]: (state: searchInterface, { payload }) => {
      return {
        ...state,
        controls: payload.controls
      };
    },
    [AD_SEARCH]: (state: searchInterface, { payload }) => ({
      ...state,
      ads: { ...payload }
    }),
    [AD_CLEAR_SEARCH]: (state: searchInterface) => ({
      ...state,
      ads: null
    }),
    [SAVED_SEARCH_GET]: (state: searchInterface, { payload }) => {
      if (!payload) {
        return {
          ...state,
          isLoadingSavedSearches: false
        };
      }
      return {
        ...state,
        savedSearches: {
          count: payload.count,
          data: payload.data,
          page: payload.page
        },
        isLoadingSavedSearches: payload.isLoading !== undefined ? payload.isLoading : false
      };
    },
    [SAVED_SEARCH_DELETE]: (state: searchInterface, { payload }) => {
      if (!payload) {
        return state;
      }
      return {
        ...state,
        savedSearches: payload.data ? payload : state.savedSearches
      };
    },
    [SAVED_SEARCH_LOADING_START]: (state: searchInterface) => ({
      ...state,
      isLoadingSavedSearches: true
    }),
    [SAVED_SEARCH_UPDATE]: setSavedSearchState,
    [CRITERIA_MODULES_UPDATE]: (state: searchInterface, { payload }) => ({
      ...state,
      criteriaModules: payload.module
        ? {
            ...state.criteriaModules,
            [payload.module]: payload.url
          }
        : {}
    }),
    [IS_FOCUS]: (state: searchInterface, { payload }) => ({
      ...state,
      isFocus: payload
    }),
    [SEARCH_LOADING]: (state: searchInterface, { payload }) => ({
      ...state,
      isSearchLoading: payload
    })
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/routes/Search/search.ducks.ts